import React, { useCallback, Fragment, useMemo } from 'react';
import { IconMenuFoldOutline, IconMenuUnfoldOutline } from '@baidu/lsqm-icon';
import { useBoolean } from 'huse';
import { Layout, Tooltip } from '@osui/ui';
import { useParams, useLocation, Link } from 'react-router-dom';
import classNames from 'classnames';
import * as pathToRegexp from 'path-to-regexp';
import OpenNewTabComponent from './OpenNewTabComponent';
import './SmartSider.less';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const {
  Sider: OSUISider
} = Layout;
const {
  compile,
  parse
} = pathToRegexp;

const generatePath = (path, params) => {
  if (path.startsWith('http')) {
    return path;
  }

  try {
    const [pathname, ...querys] = path.split('?');
    const keys = parse(pathname).reduce((total, i) => {
      if (typeof i === 'object' && i.name) {
        return { ...total,
          [i.name]: `:${i.name}`
        };
      }

      return total;
    }, {});
    return [compile(pathname)({ ...keys,
      ...params
    }), ...querys].join('?');
  } catch (e) {
    // eslint-disable-next-line
    console.log(e);
    return path;
  }
};

const MenuItem = props => {
  const {
    path,
    collapsed,
    title,
    shortTitle,
    icon,
    active,
    external,
    disabled,
    push,
    onClick
  } = props;
  const className = classNames({
    'icloud-ui-page-layout-smart-sider-menu-item-active': active,
    'icloud-ui-page-layout-smart-sider-menu-item-collapsed': collapsed
  }, 'icloud-ui-page-layout-smart-sider-menu-item');
  const main = useMemo(() => collapsed ? shortTitle === title ? /*#__PURE__*/_jsxs("div", {
    children: [icon, /*#__PURE__*/_jsx("span", {
      children: shortTitle || title
    })]
  }) : /*#__PURE__*/_jsx(Tooltip, {
    title: title,
    placement: "right",
    children: /*#__PURE__*/_jsxs("div", {
      children: [icon, /*#__PURE__*/_jsx("span", {
        children: shortTitle || title
      })]
    })
  }) : /*#__PURE__*/_jsxs(_Fragment, {
    children: [icon, /*#__PURE__*/_jsx("span", {
      children: title || shortTitle
    })]
  }), [collapsed, shortTitle, title, icon]);

  if (disabled) {
    return /*#__PURE__*/_jsx("div", {
      className: classNames(className, 'icloud-ui-page-layout-smart-sider-menu-item-disabled'),
      children: /*#__PURE__*/_jsx("div", {
        children: main
      })
    });
  }

  if (external) {
    return /*#__PURE__*/_jsxs("a", {
      className: className,
      href: path,
      target: "_blank",
      rel: "noreferrer",
      children: [main, /*#__PURE__*/_jsx(OpenNewTabComponent, {
        className: classNames({
          'external-icon': external
        })
      })]
    }, path);
  }

  if (push) {
    const pushState = () => push(path);

    return /*#__PURE__*/_jsx("a", {
      className: className,
      onClick: pushState,
      children: main
    }, path);
  }

  if (onClick && !path) {
    return /*#__PURE__*/_jsx("a", {
      className: className,
      onClick: onClick,
      children: main
    }, title);
  }

  return /*#__PURE__*/_jsx(Link, {
    className: className,
    to: path,
    children: main
  }, path);
};

const SmartSider = props => {
  const {
    icon,
    title,
    menus,
    fixed = true,
    push,
    className,
    style,
    defaultCollapsed = true,
    collapsible = true,
    onCollapse
  } = props;
  const params = useParams();
  const {
    pathname
  } = useLocation();
  const [collapsed, {
    toggle
  }] = useBoolean(defaultCollapsed);
  const onCollapseIn = useCallback((...params) => {
    toggle();
    onCollapse && onCollapse(...params);
  }, [onCollapse, toggle]);
  const renderMenuItem = useCallback(({
    children,
    path: pathIn,
    ...restProps
  }) => {
    const path = generatePath(pathIn, params);

    if (children) {
      const needExpand = children.some(i => generatePath(i.path, params) === pathname || i.active === true);
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(MenuItem, {
          push: push,
          active: generatePath(path, params) === pathname,
          collapsed: collapsed,
          path: path,
          ...restProps
        }), (needExpand || restProps.collapsed === false) && /*#__PURE__*/_jsx("div", {
          className: "icloud-ui-page-layout-smart-sider-sub-menu",
          children: children.map(item => /*#__PURE__*/_jsx(MenuItem, {
            push: push,
            active: generatePath(item.path, params) === pathname,
            collapsed: collapsed,
            ...item,
            path: generatePath(item.path, params)
          }, item.path))
        })]
      }, path);
    }

    return /*#__PURE__*/_jsx(MenuItem, {
      push: push,
      path: path,
      active: generatePath(path, params) === pathname,
      collapsed: collapsed,
      ...restProps
    });
  }, [collapsed, params, pathname, push]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(OSUISider, {
      collapsible: collapsible,
      theme: "light",
      width: 160,
      collapsed: collapsed,
      collapsedWidth: 50,
      onCollapse: onCollapseIn,
      trigger: collapsed ? /*#__PURE__*/_jsx(IconMenuUnfoldOutline, {}) : /*#__PURE__*/_jsx(IconMenuFoldOutline, {}),
      className: classNames('icloud-ui-page-layout-smart-sider', {
        'icloud-ui-page-layout-smart-sider-fixed': fixed,
        'icloud-ui-page-layout-smart-sider-collapsed': collapsed
      }, className),
      style: style,
      children: [(icon || title !== undefined) && /*#__PURE__*/_jsxs("div", {
        className: "icloud-ui-page-layout-smart-sider-header",
        children: [icon && /*#__PURE__*/_jsx("div", {
          className: "icloud-ui-page-layout-smart-sider-header-icon",
          children: icon
        }), /*#__PURE__*/_jsx("h1", {
          className: "icloud-ui-page-layout-smart-sider-header-title",
          children: title
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "icloud-ui-page-layout-smart-sider-menus",
        children: menus.map(renderMenuItem)
      })]
    }), fixed && /*#__PURE__*/_jsx("div", {
      className: classNames('icloud-ui-page-layout-smart-sider-placeholder', {
        'icloud-ui-page-layout-smart-sider-placeholder-collapsed': collapsed
      })
    })]
  });
};

export default SmartSider;
//# sourceMappingURL=es/SmartSider.js.map